import { FC } from "react";

interface Props {
	data: number[];
	ariaLabel: string;
	className?: string;
	/** Label for the values, e.g. "users". Shown alongside the range. */
	valueLabel?: string;
}

const compactNum = (v: number): string => {
	if (v >= 1_000_000) {
		const n = v / 1_000_000;
		return `${v % 1_000_000 === 0 ? n.toFixed(0) : n.toFixed(1)}M`;
	}
	if (v >= 1_000) {
		const n = v / 1_000;
		return `${v % 1_000 === 0 ? n.toFixed(0) : n.toFixed(1)}k`;
	}
	return v.toLocaleString();
};

const Sparkline: FC<Props> = ({ data, ariaLabel, className, valueLabel }) => {
	if (!data.length) {
		return (
			<div
				className={`text-xs text-muted-foreground ${className ?? ""}`}
				role="img"
				aria-label={`${ariaLabel} (no data)`}
			>
				—
			</div>
		);
	}
	const max = Math.max(...data, 1);
	const min = Math.min(...data, 0);
	const dataMin = Math.min(...data);
	const dataMax = Math.max(...data);
	const range = max - min || 1;
	const latest = data[data.length - 1];
	const points = data
		.map((v, i) => {
			const x = (i / Math.max(1, data.length - 1)) * 100;
			const y = 100 - ((v - min) / range) * 100;
			return `${x.toFixed(2)},${y.toFixed(2)}`;
		})
		.join(" ");
	return (
		<div className={className ?? ""}>
			<svg
				viewBox="0 0 100 100"
				preserveAspectRatio="none"
				className="w-full h-8"
				role="img"
				aria-label={`${ariaLabel}. Low ${compactNum(dataMin)}, latest ${compactNum(latest)}, high ${compactNum(dataMax)}${valueLabel ? ` ${valueLabel}` : ""}`}
			>
				<polyline
					points={points}
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
					vectorEffect="non-scaling-stroke"
					className="text-primary"
				/>
			</svg>
			<div className="flex justify-between text-[10px] text-muted-foreground mt-0.5">
				<span>Low {compactNum(dataMin)}</span>
				<span>
					Latest {compactNum(latest)}
					{valueLabel ? ` ${valueLabel}` : ""}
				</span>
				<span>High {compactNum(dataMax)}</span>
			</div>
		</div>
	);
};

export default Sparkline;
